import { Icon } from '@/lib/icons';
import { cn } from '@/lib/utils';
import type { SpeakContributeCopy } from '../speak-contribute-copy';
import { LiveWaveform, WaveformPlayer } from '@/features/contribution-kit';

type SpeakRecorderPanelProps = {
  copy: SpeakContributeCopy;
  recordingSuccessful: string;
  isRecording: boolean;
  isPaused: boolean;
  alreadyDone: boolean;
  recordedAudio: string | null;
  /** The microphone stream while recording, for the live waveform. */
  stream: MediaStream | null;
};

/**
 * Where the take stands, inside the phrase card: what to do, a live level
 * meter while recording, or the player to listen back before sending.
 */
export function SpeakRecorderPanel({
  copy,
  recordingSuccessful,
  isRecording,
  isPaused,
  alreadyDone,
  recordedAudio,
  stream,
}: SpeakRecorderPanelProps) {
  if (isRecording) {
    return (
      <div
        className={cn(
          'space-y-2 rounded-lg border px-4 py-3',
          isPaused ? 'border-border/60 bg-surface-2' : 'border-danger/30 bg-danger/[0.04]',
        )}
        role="status"
      >
        <p className={cn('flex items-center justify-center gap-2 text-sm font-medium', isPaused ? 'text-muted-foreground' : 'text-danger')}>
          <span className={cn('size-2 rounded-full', isPaused ? 'bg-muted-foreground' : 'animate-pulse bg-danger')} />
          {isPaused ? copy.statePaused : copy.stateRecording}
        </p>
        <LiveWaveform stream={stream} paused={isPaused} />
      </div>
    );
  }

  if (recordedAudio) {
    return (
      <div className="space-y-2 rounded-lg border border-success/25 bg-success/[0.06] p-3">
        <WaveformPlayer src={recordedAudio} />
        <p className="flex items-center gap-1.5 text-xs font-medium text-success">
          <Icon name="checkCircle" className="size-3.5" />
          {recordingSuccessful} {copy.statePreview}
        </p>
      </div>
    );
  }

  return (
    <p className={cn('rounded-lg px-4 py-3 text-center text-sm', alreadyDone ? 'bg-success/[0.06] text-success' : 'bg-surface-2 text-muted-foreground')}>
      {alreadyDone ? copy.stateDone : copy.stateIdle}
    </p>
  );
}
